import React from 'react';
import styled from 'styled-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { colors } from '@annote/core/styles';
import ExtensionBanner from 'src/components/ExtensionBanner';
import { DynamicStyles } from 'src/components/DynamicStyles';
import { DraggingProvider } from 'src/context/DraggingContext';
import { BrainProvider } from 'src/context/BrainContext';
import { SuggestedProvider } from 'src/context/SuggestedContext';
import { FocusProvider } from 'src/context/FocusContext';
import { SearchProvider } from 'src/context/SearchContext';
import { ShownIdsProvider } from 'src/context/ShownContext';
import { HighlightedReferencesProvider } from 'src/context/HighlightedReferencesContext';
import { AIProvider } from 'src/context/AIContextBackend';
import { AnnIChat } from 'src/components/AnnI/AnnIChat';
import { EditorProvider } from 'src/context/EditorContext';
import { PanelProvider, usePanelContext } from 'src/context/PanelContext';
import { LibraryPanel } from 'src/components/Panels/LibraryPanel';
import { TopNavBar } from 'src/components/Panels/TopNavBar';
import { ProfilePanel } from 'src/components/Panels/ProfilePanel';
import { ReaderPanel } from 'src/components/Panels/ReaderPanel';
import { WriterPanel } from 'src/components/Panels/WriterPanel';
import { PANEL_WIDTHS_PX } from 'src/constants/panelSizes';
import { SvgContainer } from './Surfaces/Tree/_components/Link';

const ForegroundSvgContainer = styled(SvgContainer)`
  z-index: 2010;
  pointer-events: none;
`;

const BackgroundSvgContainer = styled(SvgContainer)`
  z-index: 0;
  pointer-events: none;

  .link-line path {
    stroke-dasharray: 3, 3;
    stroke-width: 2px;
  }
`;

const Page = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  background-color: ${colors.bg.surface};
`;

const SurfaceWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

type ThinkAppProps = {
  surface: React.ReactNode;
};

// Inner component that uses panel context
const ThinkAppContent: React.FC<{ surface: React.ReactNode }> = ({ surface }) => {
  const { panels, openPanel } = usePanelContext();
  const surfaceWrapperRef = React.useRef<HTMLDivElement>(null);

  // Open library when mouse reaches left edge of screen
  React.useEffect((): (() => void) => {
    const EDGE_THRESHOLD = 10; // pixels from left edge to trigger

    const handleMouseMove = (event: MouseEvent): void => {
      // Only open if library is not already open and not pinned
      if (!panels.lib.isOpen && !panels.lib.isPinned && event.clientX <= EDGE_THRESHOLD) {
        openPanel('lib');
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return (): void => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [panels.lib.isOpen, panels.lib.isPinned, openPanel]);

  // Calculate offsets for stacked panels
  // Note: Profile panel is independent and doesn't affect stacking
  // Reader offset tracks library position dynamically so it slides with library
  // Use actual library width in pixels, convert to rem (1rem = 8px)
  const libraryWidthPx = panels.lib.isOpen ? panels.lib.width || PANEL_WIDTHS_PX.LIBRARY : 0;
  const readerOffset = libraryWidthPx / 8; // Convert pixels to rem
  const writerOffset = 0; // Writer is not stacked with anything on the right

  return (
    <Page>
      <SurfaceWrapper ref={surfaceWrapperRef} id="surface-wrapper">
        <BackgroundSvgContainer id="surface-svg-background-portal"></BackgroundSvgContainer>
        <ForegroundSvgContainer id="surface-svg-foreground-portal"></ForegroundSvgContainer>
        {surface}
        <AnnIChat />
      </SurfaceWrapper>
      <TopNavBar />
      <LibraryPanel isOpen={panels.lib.isOpen} />
      <ReaderPanel isOpen={panels.reader.isOpen} offset={readerOffset} />
      <WriterPanel isOpen={panels.writer.isOpen} offset={writerOffset} />
      <ProfilePanel isOpen={panels.profile.isOpen} />
    </Page>
  );
};

const ThinkApp: React.FC<ThinkAppProps> = ({ surface }) => {
  return (
    <>
      <ExtensionBanner />
      <DynamicStyles />
      <DndProvider backend={HTML5Backend}>
        <DraggingProvider>
          <FocusProvider>
            <SearchProvider>
              <BrainProvider>
                <ShownIdsProvider>
                  <HighlightedReferencesProvider>
                    <SuggestedProvider>
                      <AIProvider>
                        <EditorProvider>
                          <PanelProvider>
                            <ThinkAppContent surface={surface} />
                          </PanelProvider>
                        </EditorProvider>
                      </AIProvider>
                    </SuggestedProvider>
                  </HighlightedReferencesProvider>
                </ShownIdsProvider>
              </BrainProvider>
            </SearchProvider>
          </FocusProvider>
        </DraggingProvider>
      </DndProvider>
    </>
  );
};

export default ThinkApp;
